import { useState, RefObject } from 'react'

import { GenericEmbed, HTMLInputEvent, SetState } from 'types'
import { REACT_APP_IFRAMELY_API_KEY } from 'env'
import Preview from './Preview'
import { SearchForm } from '../MediaUploadPrompt/SearchForm'
import { reportError } from 'utils/error'

import Styles from '../Prompt.module.scss'

export interface Props {
  inputValue: string
  errorMessage: string
  stagedEmbed?: GenericEmbed
  inputRef: RefObject<HTMLInputElement>
  onSuccess: () => void
  setStagedEmbed: SetState<GenericEmbed | undefined>
  setInputValue: SetState<string>
  setErrorMessage: SetState<string>
}

export const EmbedFromURL = ({
  stagedEmbed,
  inputValue,
  inputRef,
  errorMessage,
  onSuccess,
  setInputValue,
  setStagedEmbed,
}: Props) => {
  const [isValidUrl, setIsValidUrl] = useState(true)

  const queryIframely = async (inputValue: string) => {
    const fullUrl = `https://iframe.ly/api/iframely?key=${REACT_APP_IFRAMELY_API_KEY}&url=${inputValue}`
    try {
      const response = await fetch(fullUrl)
      const parsedReponse = await response.json()
      const urlObject = new URL(inputValue)

      if (parsedReponse.status === 404 || parsedReponse.status === 403) {
        // page requires login
        const urlObject = new URL(inputValue)

        return {
          url: inputValue,
          provider: urlObject.hostname,
          format: 'card',
          meta: {
            title: urlObject.hostname,
          },
        }
      } else {
        return {
          ...parsedReponse,
          url: inputValue,
          provider: urlObject.hostname,
          meta: {
            title: urlObject.hostname,
          },
        }
      }
    } catch (error) {
      reportError(new Error(`Failed to call iframe.ly on ${fullUrl}`, { cause: error }))
      return undefined
    }
  }

  const validateUrl = (url: string) => {
    const isValid = new RegExp(/^https?:\/\//i).test(url)

    setIsValidUrl(isValid)

    return isValid
  }

  const handleOnChange = async (e: HTMLInputEvent) => {
    const value = extractLink(e.target.value)
    setInputValue(value)

    if (validateUrl(value)) {
      const newEmbed: GenericEmbed | undefined = await queryIframely(value)

      if (newEmbed) {
        setStagedEmbed(newEmbed)
      } else {
        setStagedEmbed(undefined)
      }
    } else {
      setStagedEmbed(undefined)
    }
  }

  return (
    <>
      <SearchForm
        inputRef={inputRef}
        placeholder="https://"
        query={inputValue}
        onChange={handleOnChange}
        onSubmit={(e) => {
          e?.preventDefault()
          const target = e?.target as HTMLInputElement

          if (validateUrl(target.value)) {
            onSuccess()
          }
        }}
        clearQuery={() => {
          setInputValue('')
          setStagedEmbed(undefined)
        }}
      />

      {inputValue ? (
        <Preview stagedEmbed={stagedEmbed} isValidEmbedUrl={isValidUrl} errorMessage={errorMessage} />
      ) : (
        <div className={Styles.previewContainer}>
          <div className={Styles.infoMessage}>
            Set your embed link to "Share with anyone" before pasting into Blaze.
          </div>
        </div>
      )}
    </>
  )
}

function extractLink(value: string) {
  const frameMatch = value.match(/^<iframe src="([^"]+)/)
  if (frameMatch) {
    return frameMatch[1]
  }
  return value
}
